// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Types
import { OcularMotilityPositionResultsType } from "src/services/exams/types";

// Utils
import { getOcularMotilityPositionLabel } from "src/utils/exams/examReportsHelper";
import { OcularMotilityPositionLayoutType } from "src/constants/enums";

type OcularMotilityTimelineType = {
  cardLayout: Array<OcularMotilityPositionLayoutType>;
  positionResults: Array<OcularMotilityPositionResultsType>;
  blinkEvents: Array<{ startDate: string; endDate: string }>;
  timing: { start: string; elapsed: number };
};

const toOffsetSeconds = (isoDate: string, startIso: string) =>
  DateTime.fromISO(isoDate).diff(DateTime.fromISO(startIso), "seconds").seconds;

const OcularMotilityTimeline = ({
  cardLayout,
  positionResults,
  blinkEvents,
  timing,
}: OcularMotilityTimelineType) => {
  const { t } = useTranslation();

  const elapsed = timing.elapsed || 1;

  // A hold bar is rarely wide enough to fit its full label without being
  // truncated, and this is a printed report -- there's no hover to reveal
  // what got cut off. So each bar just carries its walk-order index, and the
  // full label + time range is spelled out below in a legend that isn't
  // constrained by the bar's width.
  const holds = useMemo(
    () =>
      positionResults.map((result, index) => {
        const endOffset = toOffsetSeconds(result.date, timing.start);
        const startOffset = endOffset - result.holdDuration;

        return {
          key: result.focalPointPosition,
          order: index + 1,
          label: t(
            getOcularMotilityPositionLabel(
              cardLayout,
              result.focalPointPosition
            )
          ),
          leftPct: Math.max(0, (startOffset / elapsed) * 100),
          widthPct: Math.max(0, (result.holdDuration / elapsed) * 100),
          startOffset,
          endOffset,
        };
      }),
    [positionResults, cardLayout, timing.start, elapsed, t]
  );

  const blinks = useMemo(
    () =>
      blinkEvents.map((blink, index) => {
        const startOffset = toOffsetSeconds(blink.startDate, timing.start);
        const endOffset = toOffsetSeconds(blink.endDate, timing.start);

        return {
          key: `blink-${index}`,
          leftPct: Math.max(0, (startOffset / elapsed) * 100),
          widthPct: Math.max(0, ((endOffset - startOffset) / elapsed) * 100),
        };
      }),
    [blinkEvents, timing.start, elapsed]
  );

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <Box display="flex" flexDirection="column" gap="4px">
        <Box sx={{ position: "relative", height: "34px" }}>
          {/* The rounded, clipped track itself -- kept separate from the
              blink tabs below so clipping the holds to the track's rounded
              corners doesn't also clip the blinks, which are meant to poke
              out above and below it. */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "5px",
              backgroundColor: "#12141A",
              overflow: "hidden",
            }}
          >
            {holds.map((hold, index) => {
              // Each hold renders as its own independently-rounded pill, so
              // a corner that's actually flush against the track's edge
              // would still show its own rounding there -- a sliver of the
              // track peeking through. Square off just the two corners that
              // touch the track's true start/end (the parent's overflow
              // clip above then keeps them following the track's own
              // rounded silhouette) so the color fills all the way out.
              const isFirst = index === 0;
              const isLast = index === holds.length - 1;
              const borderRadius = `${isFirst ? 0 : 3}px ${isLast ? 0 : 3}px ${isLast ? 0 : 3}px ${isFirst ? 0 : 3}px`;

              return (
                <Box
                  key={hold.key}
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: `${hold.leftPct}%`,
                    width: `${Math.max(hold.widthPct, 0.5)}%`,
                    minWidth: "2px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius,
                    backgroundColor: "#4C8DFF",
                    color: "#FFF",
                    fontSize: "10px",
                    fontWeight: 600,
                    overflow: "hidden",
                  }}
                >
                  {hold.order}
                </Box>
              );
            })}
          </Box>
          {blinks.map((blink) => (
            <Box
              key={blink.key}
              sx={{
                position: "absolute",
                top: "-3px",
                bottom: "-3px",
                left: `${blink.leftPct}%`,
                width: `${Math.max(blink.widthPct, 0.3)}%`,
                minWidth: "2px",
                borderRadius: "2px",
                backgroundColor: "#6FCF97",
              }}
            />
          ))}
        </Box>

        <Box display="flex" justifyContent="space-between">
          <Typography fontSize="12px" color="#5B6D7A">
            0s
          </Typography>
          <Typography fontSize="12px" color="#5B6D7A">
            {elapsed.toFixed(1)}s
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "8px",
        }}
      >
        {holds.map((hold) => (
          <Box key={hold.key} display="flex" alignItems="baseline" gap="8px">
            <Box
              sx={{
                flexShrink: 0,
                width: "16px",
                height: "16px",
                borderRadius: "50%",
                backgroundColor: "#4C8DFF",
                color: "#FFF",
                fontSize: "10px",
                fontWeight: 600,
                lineHeight: "16px",
                textAlign: "center",
              }}
            >
              {hold.order}
            </Box>
            <Typography fontSize="12px">
              {hold.label}{" "}
              <Box component="span" color="#5B6D7A" fontSize="12px">
                ({hold.startOffset.toFixed(1)} - {hold.endOffset.toFixed(1)} s)
              </Box>
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default OcularMotilityTimeline;
